import Actions from 'actions'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom/cjs/react-router-dom.min'
import { trackEvent } from 'utils/analyticsUtils'

/**
 * Custom hook to load patient data based on URL parameters.
 *
 * Use this for components that rely on the patient from the state (`patientsReducer.patient`), like `PatientsChat.jsx`.
 *
 * @param {Object} options
 * @param {boolean} [options.disabled=false] - If true, the hook will not dispatch the patient request.
 * @param {Function} [options.onPatientRequestDispatched=() => {}] - Callback function to call when a patient request is dispatched.
 * @param {boolean} [options.sendAnalytics=true] - If true, analytics events will be sent.
 * @return - Returns the patient data from the state and a loading state.
 */
export default ({ disabled = false, onPatientRequestDispatched = () => {}, sendAnalytics = true }) => {
  const dispatch = useDispatch()
  const { patientId } = useParams()
  const { patient, isLoadingPatient, requestedPatientId } = useSelector(state => state.patientsReducer)

  useEffect(() => {
    // Check if the patientId from the URL is different from the requested patientId,
    // or if the patient in the state does not match the patientId from the URL,
    // and if the request is not disabled and the patient is not currently loading.
    if (patientId !== requestedPatientId && patient.id !== patientId && !disabled && !isLoadingPatient) {
      dispatch(Actions.requestPatient({ patientId }))
      onPatientRequestDispatched()

      if (sendAnalytics) {
        trackEvent(`Load patient from url params - requestPatient dispatched`, { patientId })
      }
    }
  }, [
    dispatch,
    patientId,
    patient.id,
    disabled,
    isLoadingPatient,
    requestedPatientId,
    onPatientRequestDispatched,
    sendAnalytics
  ])

  return {
    patient,
    isLoadingPatient
  }
}
